import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import axios from 'axios';
import {
  ArrowLeft, Users, House, Wallet, X, ShieldCheck, Tag,
  Megaphone, Gear, GridFour, ListBullets, HandHeart, SignOut
} from '@phosphor-icons/react';
import SubscriptionsView from '../components/admin/SubscriptionsView';
import InternationalView from '../components/admin/InternationalView';
import ProfileHelplineView from '../components/admin/ProfileHelplineView';
import SellerDirectPaymentsView from '../components/admin/SellerDirectPaymentsView';
import DashboardView from '../components/admin/DashboardView';
import CategoriesView from '../components/admin/CategoriesView';
import UsersView from '../components/admin/UsersView';
import ListingsView from '../components/admin/ListingsView';
import BookingsView from '../components/admin/BookingsView';
import LiveClassesView from '../components/admin/LiveClassesView';
import AdsView from '../components/admin/AdsView';
import SettingsView from '../components/admin/SettingsView';
import { LoadingSkeleton } from '../components/admin/_shared';
import { useAdminCatalog, useAdminAds, useAdminSettings, useAdminLiveClasses } from '../components/admin/adminHooks';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const SIDEBAR_ITEMS = [
  { id: 'dashboard', label: 'Dashboard', icon: GridFour, permission: 'dashboard' },
  { id: 'categories', label: 'Categories', icon: Tag, permission: 'categories' },
  { id: 'users', label: 'Users', icon: Users, permission: 'users' },
  { id: 'listings', label: 'Listings', icon: House, permission: 'listings' },
  { id: 'bookings', label: 'Bookings', icon: Wallet, permission: 'bookings' },
  { id: 'live_classes', label: 'Live Sessions', icon: HandHeart, permission: 'listings' },
  { id: 'subscriptions', label: 'Subscriptions', icon: Tag, permission: 'subscriptions' },
  { id: 'international', label: 'International', icon: Tag, permission: 'settings' },
  { id: 'profile_helpline', label: 'Profile & Helpline', icon: ShieldCheck, permission: 'settings' },
  { id: 'seller_payments', label: 'Seller Direct Payments', icon: Wallet, permission: 'settings' },
  { id: 'ads', label: 'Ads & Media', icon: Megaphone, permission: 'ads' },
  { id: 'settings', label: 'Settings', icon: Gear, permission: 'settings' },
];

const AdminPanel = () => {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [activeSection, setActiveSection] = useState('dashboard');
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [listings, setListings] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [visitStats, setVisitStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [sidebarOpen, setSidebarOpen] = useState(true);

  const catalog = useAdminCatalog();
  const adsHook = useAdminAds();
  const settingsHook = useAdminSettings();
  const liveHook = useAdminLiveClasses(activeSection);

  const isAdmin = user && (user.role === 'admin' || user.role === 'subadmin' || user.email === 'admin@spacebooking.com');
  const isMainAdmin = user && (user.role === 'admin' || user.email === 'admin@spacebooking.com');
  const userPermissions = isMainAdmin ? ['dashboard','categories','users','listings','bookings','ads','settings'] : (user?.permissions || []);
  const visibleSidebar = SIDEBAR_ITEMS.filter(item => isMainAdmin || userPermissions.includes(item.permission));

  const fetchStats = useCallback(async () => {
    try {
      const [statsRes, usersRes, listingsRes, bookingsRes, visitRes] = await Promise.all([
        axios.get(`${API_URL}/api/admin/stats`),
        axios.get(`${API_URL}/api/admin/users?limit=100`),
        axios.get(`${API_URL}/api/admin/listings?limit=100`),
        axios.get(`${API_URL}/api/admin/bookings?limit=100`),
        axios.get(`${API_URL}/api/admin/visit-stats`),
      ]);
      setStats(statsRes.data);
      setUsers(usersRes.data.users || []);
      setListings(listingsRes.data.listings || []);
      setBookings(bookingsRes.data.bookings || []);
      setVisitStats(visitRes.data);
    } catch (e) { console.error(e); }
  }, []);

  useEffect(() => {
    if (!isAdmin) return;
    setLoading(true);
    Promise.all([fetchStats(), catalog.fetchCategories(), adsHook.fetchAds(), settingsHook.fetchSettings()]).finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAdmin, fetchStats, catalog.fetchCategories, adsHook.fetchAds, settingsHook.fetchSettings]);

  // Listing actions
  const handleApproveListing = async (id) => {
    try { await axios.post(`${API_URL}/api/admin/listings/${id}/approve`, {}); setListings(prev => prev.map(l => l.listing_id === id ? { ...l, status: 'live' } : l)); } catch (e) { alert('Failed'); }
  };
  const handleRejectListing = async (id) => {
    try { await axios.post(`${API_URL}/api/admin/listings/${id}/reject`, {}); setListings(prev => prev.map(l => l.listing_id === id ? { ...l, status: 'rejected' } : l)); } catch (e) { alert('Failed'); }
  };
  const handleToggleListingStatus = async (id) => {
    try {
      const res = await axios.post(`${API_URL}/api/admin/listings/${id}/toggle-status`, {});
      setListings(prev => prev.map(l => l.listing_id === id ? { ...l, status: res.data.status } : l));
    } catch (e) { alert(e.response?.data?.detail || 'Failed'); }
  };

  // User actions
  const handleUpdateUserRole = async (userId, newRole) => {
    try { await axios.put(`${API_URL}/api/admin/users/${userId}/role?new_role=${newRole}`, {}); setUsers(prev => prev.map(u => u.user_id === userId ? { ...u, role: newRole } : u)); } catch (e) { alert('Failed'); }
  };
  const handleDeleteUser = async (userId) => {
    if (!window.confirm('Delete this user?')) return;
    try { await axios.delete(`${API_URL}/api/admin/users/${userId}`); setUsers(prev => prev.filter(u => u.user_id !== userId)); } catch (e) { alert('Failed'); }
  };
  const handleToggleUserStatus = async (userId) => {
    try {
      const res = await axios.post(`${API_URL}/api/admin/users/${userId}/toggle-status`, {});
      setUsers(prev => prev.map(u => u.user_id === userId ? { ...u, active: res.data.active } : u));
    } catch (e) { alert(e.response?.data?.detail || 'Failed'); }
  };

  if (!isAdmin) {
    return (
      <div className="min-h-screen bg-[#0f1219] flex items-center justify-center p-4">
        <div className="text-center">
          <ShieldCheck size={48} className="text-red-400 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-white mb-4">Admin Access Required</h2>
          <Button onClick={() => navigate('/buyer')} className="bg-indigo-600 hover:bg-indigo-700 rounded-xl">Go Home</Button>
        </div>
      </div>
    );
  }

  const activeCats = catalog.categories.filter(c => c.active !== false).length;
  const pendingListings = listings.filter(l => l.status === 'pending').length;

  const filteredListings = listings.filter(l => {
    if (statusFilter !== 'all' && l.status !== statusFilter) return false;
    if (searchQuery && !l.title?.toLowerCase().includes(searchQuery.toLowerCase())) return false;
    return true;
  });
  const filteredUsers = users.filter(u => {
    if (searchQuery && !u.email?.toLowerCase().includes(searchQuery.toLowerCase()) && !u.name?.toLowerCase().includes(searchQuery.toLowerCase())) return false;
    return true;
  });

  return (
    <div className="min-h-screen bg-[#0f1219] flex" data-testid="admin-panel">
      {/* Sidebar */}
      <aside className={`${sidebarOpen ? 'w-56' : 'w-0 overflow-hidden'} bg-[#161b26] border-r border-[#252d3d] flex flex-col transition-all duration-200 fixed h-full z-40 lg:relative`}>
        <div className="px-4 py-5 flex items-center justify-between border-b border-[#252d3d]">
          <div className="flex items-center gap-2.5">
            <div className="w-9 h-9 bg-indigo-600 rounded-xl flex items-center justify-center">
              <ShieldCheck size={20} className="text-white" weight="bold" />
            </div>
            <div>
              <h2 className="text-white font-bold text-sm leading-tight">L4S Admin</h2>
              <p className="text-[#6b7a99] text-[10px]">Management Portal</p>
            </div>
          </div>
          <button onClick={() => setSidebarOpen(false)} className="text-[#6b7a99] hover:text-white lg:hidden" data-testid="close-sidebar">
            <X size={18} />
          </button>
        </div>

        <nav className="flex-1 py-3 px-2 space-y-0.5">
          {visibleSidebar.map(item => {
            const Icon = item.icon;
            const isActive = activeSection === item.id;
            return (
              <button
                key={item.id}
                onClick={() => { setActiveSection(item.id); setSearchQuery(''); }}
                className={`w-full flex items-center gap-3 px-3.5 py-2.5 rounded-xl text-sm font-medium transition-all ${
                  isActive ? 'bg-indigo-600 text-white shadow-lg shadow-indigo-600/20' : 'text-[#8896b0] hover:text-white hover:bg-[#1e2536]'
                }`}
                data-testid={`sidebar-${item.id}`}
                data-nav={`admin-nav-${item.id}`}
              >
                <Icon size={20} weight={isActive ? 'fill' : 'regular'} />
                {item.label}
              </button>
            );
          })}
        </nav>

        <div className="px-4 py-3 border-t border-[#252d3d]">
          <button onClick={() => navigate('/buyer')} className="w-full flex items-center gap-2 text-[#6b7a99] hover:text-white text-xs py-2 transition-colors">
            <ArrowLeft size={14} /> Back to Site
          </button>
        </div>
      </aside>

      {/* Main Content */}
      <main className="flex-1 min-h-screen overflow-auto">
        {/* Top Bar */}
        <header className="sticky top-0 z-30 bg-[#0f1219]/80 backdrop-blur-xl border-b border-[#252d3d] px-6 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            {!sidebarOpen && (
              <button onClick={() => setSidebarOpen(true)} className="p-2 text-[#8896b0] hover:text-white rounded-lg hover:bg-[#1e2536]" data-testid="open-sidebar">
                <ListBullets size={20} />
              </button>
            )}
            <h1 className="text-white text-lg font-bold">
              {SIDEBAR_ITEMS.find(i => i.id === activeSection)?.label || visibleSidebar[0]?.label || 'Dashboard'}
            </h1>
          </div>
          <div className="flex items-center gap-4">
            <span className="text-[#6b7a99] text-xs hidden sm:inline">Logged in as <strong className="text-[#a5b4d0]">{user.email}</strong></span>
            <button
              onClick={async () => { await logout(); navigate('/admin/login'); }}
              className="flex items-center gap-2 text-[#8896b0] hover:text-white text-xs font-medium py-1.5 px-3 rounded-lg border border-[#252d3d] hover:bg-[#1e2536] transition-colors"
              data-testid="admin-logout-btn"
            >
              <SignOut size={16} /> Log Out
            </button>
          </div>
        </header>

        <div className="p-6">
          {loading ? <LoadingSkeleton /> : (
            <>
              {activeSection === 'dashboard' && <DashboardView stats={stats} visitStats={visitStats} categories={catalog.categories} activeCats={activeCats} pendingListings={pendingListings} listings={listings} bookings={bookings} handleApproveListing={handleApproveListing} handleRejectListing={handleRejectListing} />}
              {activeSection === 'categories' && (
                <CategoriesView
                  categories={catalog.categories} expandedCat={catalog.expandedCat} catServices={catalog.catServices}
                  loadingSvcs={catalog.loadingSvcs} editingIcon={catalog.editingIcon} iconUrl={catalog.iconUrl}
                  showAddCategory={catalog.showAddCategory} newCatName={catalog.newCatName} newCatIcon={catalog.newCatIcon}
                  showAddService={catalog.showAddService} newSvcName={catalog.newSvcName} newSvcPrice={catalog.newSvcPrice} newSvcDuration={catalog.newSvcDuration}
                  toggleExpand={catalog.toggleExpand} handleToggleCategory={catalog.handleToggleCategory}
                  handleSaveIcon={catalog.handleSaveIcon} handleAddCategory={catalog.handleAddCategory}
                  handleDeleteCategory={catalog.handleDeleteCategory} handleAddService={catalog.handleAddService}
                  handleToggleService={catalog.handleToggleService} handleDeleteService={catalog.handleDeleteService}
                  setEditingIcon={catalog.setEditingIcon} setIconUrl={catalog.setIconUrl}
                  setShowAddCategory={catalog.setShowAddCategory} setNewCatName={catalog.setNewCatName} setNewCatIcon={catalog.setNewCatIcon}
                  setShowAddService={catalog.setShowAddService} setNewSvcName={catalog.setNewSvcName} setNewSvcPrice={catalog.setNewSvcPrice} setNewSvcDuration={catalog.setNewSvcDuration}
                  handleMoveCategory={catalog.handleMoveCategory}
                  handleReorderCategories={catalog.handleReorderCategories}
                />
              )}
              {activeSection === 'users' && <UsersView users={filteredUsers} searchQuery={searchQuery} setSearchQuery={setSearchQuery} handleUpdateUserRole={handleUpdateUserRole} handleDeleteUser={handleDeleteUser} handleToggleUserStatus={handleToggleUserStatus} currentUser={user} />}
              {activeSection === 'listings' && <ListingsView listings={filteredListings} searchQuery={searchQuery} setSearchQuery={setSearchQuery} statusFilter={statusFilter} setStatusFilter={setStatusFilter} handleApproveListing={handleApproveListing} handleRejectListing={handleRejectListing} handleToggleListingStatus={handleToggleListingStatus} navigate={navigate} />}
              {activeSection === 'bookings' && <BookingsView bookings={bookings} />}
              {activeSection === 'live_classes' && (
                <LiveClassesView
                  liveClasses={liveHook.liveClasses}
                  sessionTypes={liveHook.sessionTypes}
                  liveClassCategories={liveHook.liveClassCategories}
                  sessionTypeCategoryFilter={liveHook.sessionTypeCategoryFilter}
                  setSessionTypeCategoryFilter={(c) => { liveHook.setSessionTypeCategoryFilter(c); liveHook.fetchSessionTypes(c); }}
                  editingClass={liveHook.editingClass}
                  editClassForm={liveHook.editClassForm}
                  showAddSessionType={liveHook.showAddSessionType}
                  newSessionTypeName={liveHook.newSessionTypeName}
                  setEditingClass={liveHook.setEditingClass}
                  setEditClassForm={liveHook.setEditClassForm}
                  setShowAddSessionType={liveHook.setShowAddSessionType}
                  setNewSessionTypeName={liveHook.setNewSessionTypeName}
                  onToggleBlock={liveHook.handleAdminToggleBlockClass}
                  onDeleteClass={liveHook.handleAdminDeleteClass}
                  onUpdateClass={liveHook.handleAdminUpdateClass}
                  onAddSessionType={liveHook.handleAddSessionType}
                  onToggleSessionType={liveHook.handleToggleSessionType}
                  onDeleteSessionType={liveHook.handleDeleteSessionType}
                  onToggleLiveClassCategory={liveHook.handleToggleLiveClassCategory}
                />
              )}
              {activeSection === 'subscriptions' && <SubscriptionsView />}
              {activeSection === 'international' && <InternationalView />}
              {activeSection === 'profile_helpline' && <ProfileHelplineView />}
              {activeSection === 'seller_payments' && <SellerDirectPaymentsView />}

              {activeSection === 'ads' && (
                <AdsView
                  ads={adsHook.ads} showAddAd={adsHook.showAddAd} adForm={adsHook.adForm} adStats={adsHook.adStats}
                  addingToPlacement={adsHook.addingToPlacement} uploadingAd={adsHook.uploadingAd}
                  listings={listings}
                  setShowAddAd={adsHook.setShowAddAd} setAdForm={adsHook.setAdForm}
                  setAddingToPlacement={adsHook.setAddingToPlacement}
                  handleCreateAd={adsHook.handleCreateAd} handleToggleAd={adsHook.handleToggleAd} handleDeleteAd={adsHook.handleDeleteAd} handleUpdateAd={adsHook.handleUpdateAd}
                  handleUploadAdMedia={adsHook.handleUploadAdMedia}
                />
              )}
              {activeSection === 'settings' && (
                <SettingsView
                  settings={settingsHook.settings}
                  subadmins={settingsHook.subadmins}
                  showAddSubadmin={settingsHook.showAddSubadmin}
                  subadminForm={settingsHook.subadminForm}
                  changingPw={settingsHook.changingPw}
                  newPw={settingsHook.newPw}
                  editingPerms={settingsHook.editingPerms}
                  editPermsValue={settingsHook.editPermsValue}
                  setShowAddSubadmin={settingsHook.setShowAddSubadmin}
                  setSubadminForm={settingsHook.setSubadminForm}
                  setChangingPw={settingsHook.setChangingPw}
                  setNewPw={settingsHook.setNewPw}
                  setEditingPerms={settingsHook.setEditingPerms}
                  setEditPermsValue={settingsHook.setEditPermsValue}
                  onToggleSetting={settingsHook.handleToggleSetting}
                  onCreateSubadmin={settingsHook.handleCreateSubadmin}
                  onToggleSubadmin={settingsHook.handleToggleSubadmin}
                  onChangePw={settingsHook.handleChangeSubadminPw}
                  onDeleteSubadmin={settingsHook.handleDeleteSubadmin}
                  onUpdatePermissions={settingsHook.handleUpdatePermissions}
                  onUploadLogo={settingsHook.handleUploadLogo}
                />
              )}
            </>
          )}
        </div>
      </main>
    </div>
  );
};

export default AdminPanel;
